import { PatternData } from "../types/siteTypes";

type PricingShape = Pick<
  PatternData,
  "price" | "discount" | "displayPricing" | "omnibus" | "displayOmnibus"
>;

export const parseAmount = (value: unknown): number => {
  if (typeof value === "number") {
    return value;
  }

  const parsed = Number(
    String(value ?? "")
      .replace(/[^0-9.,-]/g, "")
      .replace(",", ".")
  );

  return Number.isFinite(parsed) ? parsed : 0;
};

export const isDiscountActive = (price: number, discount: number): boolean =>
  price > 0 && discount > 0 && discount < price;

export const formatEur = (amount: number): string =>
  new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

export const getPatternPricingPresentation = (pattern: PricingShape) => {
  const normalizedPrice = parseAmount(pattern.displayPricing?.price ?? pattern.price);
  const normalizedDiscount = parseAmount(
    pattern.displayPricing?.discount ?? pattern.discount
  );

  const hasActiveDiscount =
    typeof pattern.displayPricing?.hasDiscount === "boolean"
      ? pattern.displayPricing.hasDiscount
      : isDiscountActive(normalizedPrice, normalizedDiscount);

  const displayEffectivePrice =
    pattern.displayPricing?.effectivePrice === null ||
    pattern.displayPricing?.effectivePrice === undefined
      ? null
      : parseAmount(pattern.displayPricing.effectivePrice);

  const fallbackCurrentEffectivePrice = hasActiveDiscount
    ? normalizedDiscount
    : normalizedPrice;

  const omnibusPayload = pattern.displayOmnibus || pattern.omnibus;
  const lowestEffectivePrice =
    omnibusPayload?.isReductionActive && omnibusPayload?.lowestEffectivePrice !== null
      ? parseAmount(omnibusPayload.lowestEffectivePrice)
      : null;
  const omnibusCurrentEffectivePrice =
    omnibusPayload?.isReductionActive && omnibusPayload?.currentEffectivePrice !== null
      ? parseAmount(omnibusPayload.currentEffectivePrice)
      : null;

  const currentEffectivePrice =
    displayEffectivePrice !== null
      ? displayEffectivePrice
      : omnibusCurrentEffectivePrice !== null
      ? omnibusCurrentEffectivePrice
      : fallbackCurrentEffectivePrice;

  const hasOmnibusReference =
    omnibusPayload?.isReductionActive === true &&
    lowestEffectivePrice !== null &&
    Number.isFinite(lowestEffectivePrice);

  const showPromotionInfo =
    hasActiveDiscount &&
    Number.isFinite(normalizedPrice) &&
    normalizedPrice > 0 &&
    currentEffectivePrice < normalizedPrice;

  const discountPercent =
    showPromotionInfo
      ? Math.ceil(
          ((normalizedPrice - currentEffectivePrice) / normalizedPrice) * 100
        )
      : 0;

  const omnibusPercent = hasActiveDiscount
    ? discountPercent
    : 0;

  return {
    normalizedPrice,
    normalizedDiscount,
    hasActiveDiscount,
    showPromotionInfo,
    promotionBasePrice: normalizedPrice,
    currentEffectivePrice,
    lowestEffectivePrice,
    showOmnibusInfo: hasActiveDiscount && hasOmnibusReference,
    showOmnibusBadge: hasActiveDiscount && omnibusPercent > 0,
    omnibusPercent,
  };
};
